import { useState, type FunctionComponent } from 'react'
import {
  Modal,
  ModalContainer,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@retailer-platform/shared-components'
import {
  Text,
  Button,
  Flex,
  CloseButton,
  spacing,
} from '@retailer-platform/shared-components/src/tds'
import { useDomainMessages } from '../utils/domain/intl'
import CustomerOrderSearchInput from './CustomerOrderSearchInput'

interface FastFindModalProps {
  onClose: () => void
  onSearch: (query: string) => void
}

const FastFindModal: FunctionComponent<FastFindModalProps> = ({ onClose, onSearch }) => {
  const [query, setQuery] = useState('')

  const messages = useDomainMessages({
    title: 'customersAndOrdersDomain.fastFind.title',
    searchLabel: 'customersAndOrdersDomain.fastFind.searchLabel',
    placeholder: 'customersAndOrdersDomain.fastFind.placeholder',
    partialSearchWarning: 'customersAndOrdersDomain.fastFind.partialSearchWarning',
    cancel: 'customersAndOrdersDomain.fastFind.cancel',
    search: 'customersAndOrdersDomain.fastFind.search',
    close: 'customersAndOrdersDomain.fastFind.close',
  })

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      maxWidth={500}
      hideCloseButton
      styleOverrides={{ overlay: { zIndex: 600 } }}
    >
      <ModalContainer>
        <ModalHeader
          css={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: spacing.s24,
            paddingRight: spacing.s24,
          }}
        >
          <Text typography="title" css={{ fontWeight: 700 }}>
            {messages.title}
          </Text>
          <CloseButton onClick={onClose} variant="secondary" aria-label={messages.close} />
        </ModalHeader>

        <ModalBody
          css={{
            paddingTop: 0,
            paddingLeft: spacing.s24,
            paddingRight: spacing.s24,
            paddingBottom: spacing.s24,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.s16,
            overflow: 'visible',
          }}
        >
          <CustomerOrderSearchInput
            onSearch={onSearch}
            onNavigate={onClose}
            onQueryChange={setQuery}
            labelProps={{ label: messages.searchLabel }}
            placeholder={messages.placeholder}
            autoFocus
          />
          <Text typography="bodyRegular" css={{ marginBottom: 0 }}>
            {messages.partialSearchWarning}
          </Text>
        </ModalBody>

        <ModalFooter>
          <Flex.Row justifyContent="flex-end" gap={spacing.s16} alignItems="center">
            <Button variant="tertiary" onClick={onClose}>
              {messages.cancel}
            </Button>
            <Button variant="primary" onClick={() => onSearch(query)} disabled={!query.trim()}>
              {messages.search}
            </Button>
          </Flex.Row>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  )
}

export default FastFindModal
